import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Lock } from "lucide-react";
import { SectionHeading } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-ink/15 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-ink/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/15";

const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60";

const TABS = [
  { id: "bookings", label: "Bookings" },
  { id: "payments", label: "Payments" },
  { id: "enquiries", label: "Enquiries" },
];

const STATUSES = ["new", "confirmed", "delivered", "collected"];

const STATUS_STYLES = {
  new: "border-amber-300 bg-amber-50 text-amber-800",
  confirmed: "border-emerald-300 bg-emerald-50 text-emerald-800",
  delivered: "border-ink/30 bg-black/[0.04] text-ink",
  collected: "border-ink/15 bg-black/[0.02] text-ink/50",
};

const Pill = ({ ok, children }) => (
  <span
    className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-semibold ${
      ok ? "bg-emerald-700/10 text-emerald-700" : "bg-amber-100 text-amber-700"
    }`}
  >
    {children}
  </span>
);

const th = "px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.15em] text-ink/50";
const td = "px-4 py-3 text-sm text-ink align-top";

const Empty = ({ label }) => (
  <p className="rounded-xl border border-dashed border-ink/15 p-8 text-center text-sm text-ink/50">
    No {label} yet.
  </p>
);

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("easyskip_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("bookings");

  const load = async (t) => {
    try {
      const res = await axios.get(`${API}/admin/summary`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem("easyskip_admin_token");
        setToken(null);
        setData(null);
      } else {
        toast.error("Could not load dashboard data.");
      }
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem("easyskip_admin_token", res.data.token);
      setToken(res.data.token);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed — check your details.");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("easyskip_admin_token");
    setToken(null);
    setData(null);
  };

  const updateStatus = async (ref, status) => {
    try {
      await axios.patch(
        `${API}/admin/bookings/${ref}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData((d) => ({
        ...d,
        bookings: d.bookings.map((b) => (b.reference === ref ? { ...b, status } : b)),
      }));
      toast.success(`${ref} marked as ${status}`);
    } catch {
      toast.error("Could not update the booking status.");
    }
  };

  if (!token) {
    return (
      <section className="mx-auto flex max-w-6xl justify-center px-5 py-20 sm:px-8">
        <div className="w-full max-w-sm rounded-2xl border border-ink/10 bg-white p-8 shadow-[0_16px_50px_rgba(0,0,0,0.05)]">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-700/10">
            <Lock className="h-5 w-5 text-emerald-700" />
          </div>
          <h1 className="mt-5 font-heading text-2xl font-extrabold text-ink">Easyskip Admin</h1>
          <p className="mt-1 text-sm text-ink/55">Sign in to see bookings, payments and enquiries.</p>
          <form onSubmit={login} className="mt-6 space-y-4" data-testid="admin-login-form">
            <div>
              <label className={labelCls} htmlFor="ad-email">Email</label>
              <input
                id="ad-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="ad-pass">Password</label>
              <input
                id="ad-pass"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
              />
            </div>
            {error && (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={busy}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3.5 font-heading text-sm font-bold text-white transition-colors duration-300 hover:bg-emerald-800 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </button>
          </form>
        </div>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
      </section>
    );
  }

  const revenue =
    data.payments
      .filter((p) => p.payment_status === "paid")
      .reduce((s, p) => s + (p.amount || 0), 0) / 100;

  return (
    <section className="mx-auto max-w-6xl px-5 py-12 sm:px-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <SectionHeading eyebrow="Admin" title="Dashboard." />
        <div className="mb-14 flex gap-3">
          <button
            data-testid="admin-refresh-button"
            onClick={() => load(token)}
            className="flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-white"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-white"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-ink/10 p-6" data-testid="stat-bookings">
          <p className="font-heading text-3xl font-black text-ink">{data.bookings.length}</p>
          <p className="mt-1 text-xs text-ink/55">Bookings</p>
        </div>
        <div className="rounded-2xl border border-ink/10 p-6" data-testid="stat-revenue">
          <p className="font-heading text-3xl font-black text-emerald-700">€{revenue}</p>
          <p className="mt-1 text-xs text-ink/55">Paid online</p>
        </div>
        <div className="rounded-2xl border border-ink/10 p-6" data-testid="stat-enquiries">
          <p className="font-heading text-3xl font-black text-ink">{data.enquiries.length}</p>
          <p className="mt-1 text-xs text-ink/55">Enquiries</p>
        </div>
      </div>

      <div className="mt-8 flex gap-2 border-b border-ink/10">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 font-heading text-sm font-bold transition-colors ${
              tab === t.id
                ? "border-b-2 border-emerald-700 text-emerald-700"
                : "text-ink/50 hover:text-ink"
            }`}
          >
            {t.label} ({data[t.id].length})
          </button>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto">
        {tab === "bookings" &&
          (data.bookings.length === 0 ? (
            <Empty label="bookings" />
          ) : (
            <table className="w-full min-w-[860px] border-collapse" data-testid="admin-bookings-table">
              <thead>
                <tr className="border-b border-ink/10">
                  <th className={th}>Ref</th>
                  <th className={th}>Customer</th>
                  <th className={th}>Skip</th>
                  <th className={th}>Delivery</th>
                  <th className={th}>Price</th>
                  <th className={th}>Payment</th>
                  <th className={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.bookings.map((b) => (
                  <tr key={b.reference} className="border-b border-ink/5 hover:bg-black/[0.02]">
                    <td className={`${td} font-mono text-xs font-semibold`}>{b.reference}</td>
                    <td className={td}>
                      <p className="font-semibold">{b.name}</p>
                      <p className="text-xs text-ink/55">{b.phone}</p>
                      <p className="text-xs text-ink/55">{b.address}</p>
                    </td>
                    <td className={td}>{b.skip_label}</td>
                    <td className={td}>{b.delivery_date}</td>
                    <td className={td}>{b.price ? `€${b.price}` : "—"}</td>
                    <td className={td}>
                      {b.payment_status === "paid" ? <Pill ok>Paid</Pill> : <Pill>Unpaid</Pill>}
                    </td>
                    <td className={td}>
                      <select
                        data-testid={`status-select-${b.reference}`}
                        value={b.status || "new"}
                        onChange={(e) => updateStatus(b.reference, e.target.value)}
                        className={`cursor-pointer appearance-none rounded-full border px-3 py-1.5 text-xs font-semibold outline-none transition-colors ${STATUS_STYLES[b.status || "new"]}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s} className="bg-white text-ink">
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}

        {tab === "payments" &&
          (data.payments.length === 0 ? (
            <Empty label="payments" />
          ) : (
            <table className="w-full min-w-[640px] border-collapse" data-testid="admin-payments-table">
              <thead>
                <tr className="border-b border-ink/10">
                  <th className={th}>Session</th>
                  <th className={th}>Booking</th>
                  <th className={th}>Amount</th>
                  <th className={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.payments.map((p) => (
                  <tr key={p.session_id} className="border-b border-ink/5 hover:bg-black/[0.02]">
                    <td className={`${td} font-mono text-xs`}>…{p.session_id.slice(-8)}</td>
                    <td className={`${td} font-mono text-xs`}>{p.booking_ref}</td>
                    <td className={td}>€{(p.amount || 0) / 100}</td>
                    <td className={td}>
                      {p.payment_status === "paid" ? (
                        <Pill ok>Paid</Pill>
                      ) : (
                        <Pill>{p.payment_status}</Pill>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}

        {tab === "enquiries" &&
          (data.enquiries.length === 0 ? (
            <Empty label="enquiries" />
          ) : (
            <table className="w-full min-w-[760px] border-collapse" data-testid="admin-enquiries-table">
              <thead>
                <tr className="border-b border-ink/10">
                  <th className={th}>Ref</th>
                  <th className={th}>Subject</th>
                  <th className={th}>From</th>
                  <th className={th}>Message</th>
                </tr>
              </thead>
              <tbody>
                {data.enquiries.map((m) => (
                  <tr key={m.reference} className="border-b border-ink/5 hover:bg-black/[0.02]">
                    <td className={`${td} font-mono text-xs font-semibold`}>{m.reference}</td>
                    <td className={td}>{m.subject}</td>
                    <td className={td}>
                      <p className="font-semibold">{m.name}</p>
                      <p className="text-xs text-ink/55">{m.email}</p>
                      {m.phone && <p className="text-xs text-ink/55">{m.phone}</p>}
                    </td>
                    <td className={`${td} max-w-xs`}>
                      <p className="line-clamp-3 text-ink/75">{m.message}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
      </div>
    </section>
  );
}
